import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEmpresaContext } from '../context/EmpresaContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Building2, Mail, Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import useDynamicFavicon from '../hooks/useDynamicFavicon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores del tema por defecto
const DEFAULT_COLORS = {
  primario: '#4A6B6B',
  secundario: '#3D5A5A',
  terciario: '#5A7A7A'
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { empresaCodigo } = useParams(); // Obtener código de empresa de la URL
  const { login, user } = useAuth();
  const { esDominioEmpresa, empresaDominio } = useEmpresaContext();
  
  const [empresas, setEmpresas] = useState([]);
  const [empresaDirecta, setEmpresaDirecta] = useState(null); // Empresa cargada por URL
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingEmpresaDirecta, setLoadingEmpresaDirecta] = useState(!!empresaCodigo);
  const [error, setError] = useState('');
  const [themeColors, setThemeColors] = useState(DEFAULT_COLORS);

  const [formData, setFormData] = useState({
    empresa: '',
    correo: '',
    password: '',
  });

  // Código de empresa efectivo: dominio > URL param
  const empresaCodigoEfectivo = esDominioEmpresa ? empresaDominio?.codigo : empresaCodigo;

  // Favicon dinámico: dominio > URL param > selección dropdown
  const faviconCodigo = empresaCodigoEfectivo || (formData.empresa !== '__superadmin__' ? formData.empresa : null);
  useDynamicFavicon(faviconCodigo);

  // Redireccionar si ya está logueado
  useEffect(() => {
    if (user) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  // Si hay empresa por dominio, usarla como empresa directa
  useEffect(() => {
    if (esDominioEmpresa && empresaDominio) {
      setEmpresaDirecta(empresaDominio);
      setFormData(prev => ({ ...prev, empresa: empresaDominio.codigo }));
      setLoadingEmpresaDirecta(false);
      if (empresaDominio.colores) {
        setThemeColors({
          primario: empresaDominio.colores.primario || DEFAULT_COLORS.primario,
          secundario: empresaDominio.colores.secundario || DEFAULT_COLORS.secundario,
          terciario: empresaDominio.colores.terciario || DEFAULT_COLORS.terciario
        });
      }
    }
  }, [esDominioEmpresa, empresaDominio]);

  // Cargar empresa directa por URL si hay código (solo si no hay dominio)
  useEffect(() => {
    if (empresaCodigo && !esDominioEmpresa) {
      const fetchEmpresaDirecta = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/auth/empresa/${empresaCodigo}`);
          setEmpresaDirecta(response.data);
          setFormData(prev => ({ ...prev, empresa: response.data.codigo }));
          
          // Aplicar colores de la empresa
          if (response.data.colores) {
            setThemeColors({
              primario: response.data.colores.primario || DEFAULT_COLORS.primario,
              secundario: response.data.colores.secundario || DEFAULT_COLORS.secundario,
              terciario: response.data.colores.terciario || DEFAULT_COLORS.terciario
            });
          }
        } catch (err) {
          console.error('Error fetching empresa:', err);
          setError('Empresa no encontrada. Seleccione una empresa de la lista.');
          // Redirigir al login general después de 2 segundos
          setTimeout(() => navigate('/login'), 2000);
        } finally {
          setLoadingEmpresaDirecta(false);
        }
      };
      fetchEmpresaDirecta();
    }
  }, [empresaCodigo, navigate, esDominioEmpresa]);

  // Cargar lista de empresas (solo si no hay empresa directa)
  useEffect(() => {
    if (!empresaCodigoEfectivo) {
      const fetchEmpresas = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/auth/empresas`);
          setEmpresas(response.data);
        } catch (err) {
          console.error('Error fetching empresas:', err);
        } finally {
          setLoadingEmpresas(false);
        }
      };
      fetchEmpresas();
    } else {
      setLoadingEmpresas(false);
    }
  }, [empresaCodigoEfectivo]);

  // Actualizar colores cuando se selecciona una empresa del dropdown
  const handleEmpresaChange = (empresaCodigoSelected) => {
    setFormData({ ...formData, empresa: empresaCodigoSelected });
    
    const selectedEmpresa = empresas.find(e => e.codigo === empresaCodigoSelected);
    if (selectedEmpresa?.colores) {
      setThemeColors({
        primario: selectedEmpresa.colores.primario || DEFAULT_COLORS.primario,
        secundario: selectedEmpresa.colores.secundario || DEFAULT_COLORS.secundario,
        terciario: selectedEmpresa.colores.terciario || DEFAULT_COLORS.terciario
      });
    } else {
      setThemeColors(DEFAULT_COLORS);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.empresa, formData.correo, formData.password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // Determinar si mostrar el dropdown o no
  const showDropdown = !empresaCodigoEfectivo || !empresaDirecta;
  
  // Obtener datos de la empresa para mostrar (directa o seleccionada)
  const empresaActual = empresaDirecta || empresas.find(e => e.codigo === formData.empresa);

  // Loading mientras carga empresa directa
  if (loadingEmpresaDirecta && !esDominioEmpresa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1636142466028-8c6c7cd616b7?crop=entropy&cs=srgb&fm=jpg&q=85)' 
        }}
      >
        <div 
          className="absolute inset-0" 
          style={{ backgroundColor: `${themeColors.terciario}E6` }}
        />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <div className="max-w-md text-center">
            <h1 className="font-heading text-4xl font-bold mb-4">
              Libro de Reclamaciones Virtual
            </h1>
            <p className="text-lg text-white/80">
              Sistema de gestión de reclamos para el sector inmobiliario
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="space-y-1 text-center">
            {/* Logo o Ícono */}
            <div className="flex justify-center mb-4">
              {empresaActual?.logo_url ? (
                <div className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden bg-white border border-slate-200 p-2">
                  <img 
                    src={empresaActual.logo_url} 
                    alt={`Logo ${empresaActual.nombre}`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center transition-colors duration-200"
                  style={{ backgroundColor: themeColors.terciario }}
                >
                  <Building2 className="w-8 h-8 text-white" />
                </div>
              )}
            </div>
            
            <CardTitle className="font-heading text-2xl font-bold">
              Panel de Administración
            </CardTitle>
            
            {/* Nombre de la empresa si está seleccionada */}
            {empresaActual && empresaActual.codigo !== '__superadmin__' && (
              <p 
                className="text-sm font-medium"
                style={{ color: themeColors.terciario }}
              >
                {empresaActual.nombre}
              </p>
            )}
            
            <CardDescription>
              Ingrese sus credenciales para acceder
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fadeIn">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Dropdown de empresa - solo si no viene por URL */}
              {showDropdown && (
                <div className="space-y-2">
                  <Label htmlFor="empresa" className="text-sm font-medium text-slate-700">
                    Empresa
                  </Label>
                  <Select
                    value={formData.empresa}
                    onValueChange={handleEmpresaChange}
                  >
                    <SelectTrigger 
                      id="empresa"
                      data-testid="empresa-select"
                      className="h-11 border-slate-200"
                      style={{ 
                        '--tw-ring-color': themeColors.primario 
                      }}
                    >
                      <SelectValue placeholder={loadingEmpresas ? "Cargando..." : "Seleccione una empresa"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__superadmin__">SUPERADMIN (Sin empresa)</SelectItem>
                      {empresas.map((emp) => (
                        <SelectItem key={emp.id} value={emp.codigo}>
                          {emp.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="correo" className="text-sm font-medium text-slate-700">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="correo"
                    type="email"
                    placeholder="usuario@empresa.com"
                    data-testid="correo-input"
                    className="pl-10 h-11 border-slate-200"
                    value={formData.correo}
                    onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                    required
                    style={{ 
                      '--tw-ring-color': themeColors.primario 
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    data-testid="password-input"
                    className="pl-10 h-11 border-slate-200"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    style={{ 
                      '--tw-ring-color': themeColors.primario 
                    }}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 text-white font-semibold transition-all duration-200"
                style={{ 
                  backgroundColor: themeColors.primario,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.secundario;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.primario;
                }}
                disabled={loading || (!empresaCodigoEfectivo && !formData.empresa)}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>

            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
